import { Button } from '@heidi/design-system/button';
import { FileCard } from '@heidi/design-system/file-card';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { projectChatComposerMessages } from '../../data';
import { ProjectComposerLoadingFileCard } from '../projects/components/ProjectComposerLoadingFileCard';
import {
  formatComposerAttachmentType,
  type ComposerAttachmentItem,
} from './composer-attachments';

export interface AttachmentRowProps {
  items: readonly ComposerAttachmentItem[];
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
  renderImageItem?: (item: ComposerAttachmentItem, index: number) => ReactNode;
  testId?: string;
}

export const AttachmentRow = ({
  items,
  onRemove,
  onRetry,
  renderImageItem,
  testId,
}: AttachmentRowProps): JSX.Element | null => {
  const intl = useIntl();
  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {items.map((item, index) => {
        const fileType = formatComposerAttachmentType(item.name);
        if (item.source.kind === 'file') {
          return (
            <div key={item.id} className="relative">
              <FileCard fileName={item.name} fileType={fileType} size="default" />
              <Button
                type="button"
                variant="ghost"
                size="xs"
                iconOnly
                onClick={() => onRemove(item.id)}
                aria-label={intl.formatMessage(
                  projectChatComposerMessages.removeFileAria,
                  { name: item.name }
                )}
                className={cn(
                  'absolute -end-1 -top-1 size-5 rounded-full',
                  'border border-border bg-surface text-text-secondary shadow-sm',
                  'hover:border-border-hover hover:text-text-primary'
                )}
              >
                <X className="size-3" />
              </Button>
            </div>
          );
        }

        const isError = item.status === 'error';
        const card = item.type.startsWith('image/') ? (
          renderImageItem?.(item, index)
        ) : item.status === 'uploading' ? (
          <ProjectComposerLoadingFileCard
            fileName={item.name}
            fileType={fileType}
            loadingProgress={item.progress}
            onDelete={() => onRemove(item.id)}
          />
        ) : (
          <FileCard
            fileName={item.name}
            fileType={fileType}
            size="default"
            variant={isError ? 'error' : 'default'}
            errorMessage={item.error}
            onDelete={() => onRemove(item.id)}
            onRetry={isError && onRetry ? () => onRetry(item.id) : undefined}
          />
        );

        return <div key={item.id}>{card}</div>;
      })}
    </div>
  );
};
